import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, Clock, Mail, X, CheckCircle, Loader2 } from 'lucide-react';
import { canAccessPage, getRoleConfig } from '../../data/roleConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for auth verification to avoid repeated checks
const authCache = {
  admin: { verified: false, timestamp: 0 },
  worker: { verified: false, timestamp: 0 }
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Session Expired Modal Component (inline to avoid circular dependencies)
const SessionExpiredInline = ({ email, onGoToLogin }) => {
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSendMagicLink = async () => {
    if (!email) {
      onGoToLogin();
      return;
    }
    setStatus('sending');
    try {
      await axios.post(`${API_URL}/api/auth/request-magic-link`, null, { params: { email } });
      setStatus('sent');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Error al enviar el enlace');
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100">
        {status === 'idle' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">Tu sesión ha expirado</h2>
            <p className="text-gray-500 text-center mb-6">Por tu seguridad, tu sesión ha expirado.</p>
            {email && (
              <p className="text-sm text-gray-400 text-center mb-4">
                Te enviaremos un nuevo enlace de acceso a:<br />
                <span className="font-medium text-gray-600">{email}</span>
              </p>
            )}
            <div className="space-y-3">
              <button
                onClick={handleSendMagicLink}
                className="w-full bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg shadow-[#e03c3c]/20"
                data-testid="send-magic-link-btn"
              >
                <Mail className="w-5 h-5" />
                Enviar enlace de acceso
              </button>
              <button onClick={onGoToLogin} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium">
                Ir a la página de login
              </button>
            </div>
          </>
        )}
        {status === 'sending' && (
          <div className="py-8 text-center">
            <Loader2 className="w-12 h-12 text-[#e03c3c] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Enviando enlace de acceso...</p>
          </div>
        )}
        {status === 'sent' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">¡Enlace enviado!</h2>
            <p className="text-gray-500 text-center mb-2">Revisa tu correo electrónico:</p>
            <p className="font-medium text-gray-900 text-center mb-6">{email}</p>
            <p className="text-sm text-gray-400 text-center mb-6">Haz clic en el enlace del correo para continuar.</p>
            <button onClick={onGoToLogin} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium">Entendido</button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">Error al enviar</h2>
            <p className="text-gray-500 text-center mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <button onClick={() => setStatus('idle')} className="w-full bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white py-3 rounded-xl font-medium">Intentar de nuevo</button>
              <button onClick={onGoToLogin} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium">Ir a login</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * ProtectedAdminRoute - Verifies admin authentication before rendering children
 * Now supports system_role: admin, supervisor, ventas
 * If role has no access to current page, redirects to their home
 */
export const ProtectedAdminRoute = ({ children }) => {
  const [authState, setAuthState] = useState('checking'); // 'checking', 'authenticated', 'unauthenticated', 'session-expired'
  const [expiredEmail, setExpiredEmail] = useState(null);
  const location = useLocation();

  const verifyToken = useCallback(async () => {
    // JWT lives in the httpOnly cookie. As a UI hint we check portal_user
    // (non-sensitive); the authoritative check is the backend /api/auth/verify
    // call below which reads the cookie via withCredentials.
    const userHint = localStorage.getItem('portal_user');

    // Check cache first
    const now = Date.now();
    if (authCache.admin.verified && (now - authCache.admin.timestamp) < CACHE_DURATION) {
      setAuthState('authenticated');
      return;
    }

    try {
      // Verify session with backend — cookie is sent automatically.
      const response = await axios.get(`${API_URL}/api/auth/verify`, {
        withCredentials: true,
      });

      if (response.data && response.data.valid && response.data.role === 'admin') {
        // Store system_role in portal_user
        const systemRole = response.data.system_role || 'admin';
        try {
          const currentUser = JSON.parse(userHint || '{}');
          if (currentUser.system_role !== systemRole) {
            currentUser.system_role = systemRole;
            localStorage.setItem('portal_user', JSON.stringify(currentUser));
          }
        } catch (e) { /* ignore */ }
        
        authCache.admin = { verified: true, timestamp: now };
        setAuthState('authenticated');
      } else if (response.data && !response.data.valid && response.data.reason === 'TOKEN_EXPIRED') {
        // Token is expired - show session expired UI with magic link option
        setExpiredEmail(response.data.email);
        authCache.admin = { verified: false, timestamp: 0 };
        setAuthState('session-expired');
      } else if (response.data && response.data.valid && response.data.role !== 'admin') {
        // User has valid session but wrong role - clear and redirect
        localStorage.removeItem('portal_user');
        localStorage.removeItem('worker_user');
        authCache.admin = { verified: false, timestamp: 0 };
        setAuthState('unauthenticated');
      } else {
        // Token is invalid
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        authCache.admin = { verified: false, timestamp: 0 };
        setAuthState('unauthenticated');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Check if it's specifically a token expired error
        const detail = error.response?.data?.detail;
        if (detail === 'TOKEN_EXPIRED') {
          try {
            const userData = JSON.parse(localStorage.getItem('portal_user') || '{}');
            setExpiredEmail(userData.email);
          } catch (e) { /* ignore */ }
          authCache.admin = { verified: false, timestamp: 0 };
          setAuthState('session-expired');
        } else {
          localStorage.removeItem('portal_token');
          localStorage.removeItem('portal_user');
          localStorage.removeItem('worker_token');
          authCache.admin = { verified: false, timestamp: 0 };
          setAuthState('unauthenticated');
        }
      } else {
        authCache.admin = { verified: true, timestamp: now };
        setAuthState('authenticated');
      }
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleGoToLogin = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    localStorage.removeItem('worker_token');
    window.location.href = '/login';
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#e03c3c] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando sesion...</p>
        </div>
      </div>
    );
  }

  if (authState === 'session-expired') {
    return <SessionExpiredInline email={expiredEmail} onGoToLogin={handleGoToLogin} />;
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check page-level access for non-admin roles
  try {
    const user = JSON.parse(localStorage.getItem('portal_user') || '{}');
    const systemRole = user.system_role || 'admin';
    
    if (systemRole !== 'admin') {
      // Extract page id from current path
      const pathParts = location.pathname.replace('/admin/', '').replace('/admin', '').split('/');
      const pageId = pathParts[0] || 'dashboard';
      
      if (!canAccessPage(systemRole, pageId)) {
        // Redirect to their home page
        const config = getRoleConfig(systemRole);
        return <Navigate to={config.homePath} replace />;
      }
    }
  } catch (e) { /* ignore, allow access */ }

  // Authenticated - render the protected content
  return children;
};

/**
 * ProtectedWorkerRoute - Verifies worker authentication
 */
export const ProtectedWorkerRoute = ({ children }) => {
  const [authState, setAuthState] = useState('checking');
  const [expiredEmail, setExpiredEmail] = useState(null);
  const location = useLocation();

  const verifyToken = useCallback(async () => {
    // JWT lives in httpOnly cookie. Cache check + backend verify (with credentials).
    // Check cache first
    const now = Date.now();
    if (authCache.worker.verified && (now - authCache.worker.timestamp) < CACHE_DURATION) {
      setAuthState('authenticated');
      return;
    }

    try {
      // Verify session with backend — cookie sent automatically.
      const response = await axios.get(`${API_URL}/api/auth/verify`, {
        withCredentials: true,
      });

      if (response.data && response.data.valid && (response.data.role === 'worker' || response.data.role === 'admin')) {
        // JWT lives in cookie — no localStorage write needed.
        authCache.worker = { verified: true, timestamp: now };
        setAuthState('authenticated');
      } else if (response.data && !response.data.valid && response.data.reason === 'TOKEN_EXPIRED') {
        // Token is expired - show session expired UI
        setExpiredEmail(response.data.email);
        authCache.worker = { verified: false, timestamp: 0 };
        setAuthState('session-expired');
      } else {
        localStorage.removeItem('portal_user');
        localStorage.removeItem('worker_user');
        authCache.worker = { verified: false, timestamp: 0 };
        setAuthState('unauthenticated');
      }
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        const detail = error.response?.data?.detail;
        if (detail === 'TOKEN_EXPIRED') {
          try {
            const userData = JSON.parse(localStorage.getItem('portal_user') || '{}');
            setExpiredEmail(userData.email);
          } catch (e) { /* ignore */ }
          authCache.worker = { verified: false, timestamp: 0 };
          setAuthState('session-expired');
        } else {
          localStorage.removeItem('portal_user');
          localStorage.removeItem('worker_user');
          authCache.worker = { verified: false, timestamp: 0 };
          setAuthState('unauthenticated');
        }
      } else {
        authCache.worker = { verified: true, timestamp: now };
        setAuthState('authenticated');
      }
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleGoToLogin = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    localStorage.removeItem('worker_token');
    window.location.href = '/login';
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#e03c3c] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando sesion...</p>
        </div>
      </div>
    );
  }

  if (authState === 'session-expired') {
    return <SessionExpiredInline email={expiredEmail} onGoToLogin={handleGoToLogin} />;
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};
